import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { List, ListItem, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export interface PasswordValidationError {
  letter: boolean;
  number: boolean;
  specialChar: boolean;
  minLength: boolean;
}

export enum PasswordValidationKey {
  Letter = 'letter',
  Number = 'number',
  SpecialChar = 'specialChar',
  MinLength = 'minLength',
}

export const passwordValidationSchema = yup
  .string()
  .trim()
  .test((value, ctx) => {
    const transformedValue = value ?? '';

    const letterRegex = new RegExp(/(?=.*[a-zA-Z])/);
    const numberRegex = new RegExp(/(?=.*\d)/);
    const specialCharRegex = new RegExp(/(?=.*[@$!%*#?&])/);

    const hasLetter = letterRegex.test(transformedValue);
    const hasNumber = numberRegex.test(transformedValue);
    const hasSpecialChar = specialCharRegex.test(transformedValue);
    const passMinLength = transformedValue.length >= 8;

    if (!(hasLetter && hasNumber && hasSpecialChar && passMinLength)) {
      return ctx.createError({
        message: {
          letter: !hasLetter,
          number: !hasNumber,
          specialChar: !hasSpecialChar,
          minLength: !passMinLength,
        },
      });
    }

    return true;
  });

export const PasswordValidationWarning = ({ error }: { error: PasswordValidationError }) => {
  const { t } = useTranslation();
  const warningMessages = useMemo(
    () => [
      {
        key: PasswordValidationKey.Letter,
        message: t('passwordValidationWarning.containAtLeastOneLetter'),
      },
      {
        key: PasswordValidationKey.Number,
        message: t('passwordValidationWarning.containAtLeastOneNumber'),
      },
      {
        key: PasswordValidationKey.SpecialChar,
        message: t('passwordValidationWarning.containAtLeastOneSpecialCharacter'),
      },
      {
        key: PasswordValidationKey.MinLength,
        message: t('passwordValidationWarning.beAtLeast8CharactersLong'),
      },
    ],
    [t]
  );

  return (
    <div>
      <Typography variant="body2">{t('passwordValidationWarning.passwordMust')}</Typography>
      <List>
        {warningMessages.map(({ key, message }) => {
          const hasError = error[key];
          return (
            <ListItem key={key} disablePadding sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: hasError ? 'error.main' : 'success.main',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {hasError ? <ErrorIcon fontSize="inherit" /> : <CheckIcon fontSize="inherit" />}
                {message}
              </Typography>
            </ListItem>
          );
        })}
      </List>
    </div>
  );
};
